import React, { useState, useEffect } from 'react';
import {
    Users,
    MapPin,
    Building2,
    Home,
    DollarSign,
    Bed,
    Maximize,
    Clock,
    Lightbulb,
    CreditCard,
    RefreshCw,
    UserX,
    PhoneCall
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';
import { fetchBuyerPreferencesAnalytics } from '../api/analyticsApi';
import BuyerPreferenceMatchesModal from './BuyerPreferenceMatchesModal';
import DeactivatedBuyersModal from './DeactivatedBuyersModal';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const BuyerAnalytics = ({ onBuyerClick, startDate, endDate, variant = 'buyer' }) => {
    const isGeevee = variant === 'geevee';
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [drilldown, setDrilldown] = useState({ open: false, filterType: null, filterValue: null });
    const [showAllSuburbs, setShowAllSuburbs] = useState(false);
    const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const result = await fetchBuyerPreferencesAnalytics(startDate, endDate);
                if (!cancelled) setData(result);
            } catch (error) {
                console.error('Error loading buyer preferences analytics:', error);
                if (!cancelled) toast.error('Failed to load buyer preferences');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    const openDrilldown = (filterType, filterValue) => {
        if (filterValue == null || filterValue === '') return;
        setDrilldown({ open: true, filterType, filterValue });
    };

    const closeDrilldown = () => setDrilldown((d) => ({ ...d, open: false }));

    if (loading) {
        if (isGeevee) {
            return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <div className="flex items-center justify-center py-12 text-gray-500">
                        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading property preferences…
                    </div>
                </div>
            );
        }
        return (
            <div className="flex items-center justify-center p-12">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (!data) return null;

    const PreferenceCard = ({ title, icon: Icon, children, className = "", headerAction = null }) => (
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-500" />
                    <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">{title}</h3>
                </div>
                {headerAction}
            </div>
            <div className="p-4">
                {children}
            </div>
        </div>
    );

    const ListPreference = ({ items, filterType, labelKey = 'name', countKey = 'count', maxItems = 8 }) => (
        <div className={`space-y-3 ${maxItems > 8 ? 'max-h-80 overflow-y-auto pr-1' : ''}`}>
            {items.slice(0, maxItems).map((item, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={() => openDrilldown(filterType, item[labelKey])}
                    className="w-full flex items-center justify-between text-left rounded-md -mx-1 px-1 py-1 hover:bg-blue-50 transition-colors cursor-pointer"
                    title={`Show buyers with ${item[labelKey]}`}
                >
                    <span className="text-sm font-medium text-gray-700 truncate mr-4 hover:text-blue-700">{item[labelKey]}</span>
                    <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500"
                                style={{ width: `${(item[countKey] / data.total_buyers) * 100}%` }}
                            />
                        </div>
                        <span className="text-xs font-bold text-gray-500 w-8 text-right">{item[countKey]}</span>
                    </div>
                </button>
            ))}
        </div>
    );

    const deactivatedCount = data.deactivated_count || 0;
    const deactivationRangeApplied = Boolean(data.deactivated_date_range_applied);

    const preferenceCharts = (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <PreferenceCard title="City Preferences" icon={Building2} className="lg:col-span-1">
                {(data.cities && data.cities.length > 0) ? (
                    <ListPreference items={data.cities} filterType="city" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No city preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard
                title="Suburb Preferences"
                icon={MapPin}
                className="lg:col-span-1"
                headerAction={data.suburbs && data.suburbs.length > 8 ? (
                    <button
                        type="button"
                        onClick={() => setShowAllSuburbs((v) => !v)}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        {showAllSuburbs ? 'Show top 8' : `Show all (${data.suburbs.length})`}
                    </button>
                ) : null}
            >
                {(data.suburbs && data.suburbs.length > 0) ? (
                    <ListPreference
                        items={data.suburbs}
                        filterType="suburb"
                        maxItems={showAllSuburbs ? data.suburbs.length : 8}
                    />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No suburb preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Property Type" icon={Home} className="lg:col-span-1">
                <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                        <Pie
                            data={data.property_types}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="count"
                            onClick={(entry) => openDrilldown('property_type', entry?.name)}
                            style={{ cursor: 'pointer' }}
                        >
                            {data.property_types.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4 justify-center mt-2">
                    {data.property_types.map((entry, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => openDrilldown('property_type', entry.name)}
                            className="flex items-center gap-1.5 hover:bg-gray-50 rounded px-1 py-0.5 transition-colors cursor-pointer"
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                            <span className="text-xs font-medium text-gray-600 hover:text-blue-700">{entry.name} ({entry.count})</span>
                        </button>
                    ))}
                </div>
            </PreferenceCard>

            <PreferenceCard title="Max. Bedrooms" icon={Bed} className="lg:col-span-1">
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.bedrooms}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Bar
                            dataKey="count"
                            fill="#3B82F6"
                            radius={[4, 4, 0, 0]}
                            onClick={(entry) => openDrilldown('bedrooms', entry?.name)}
                            style={{ cursor: 'pointer' }}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </PreferenceCard>

            <PreferenceCard title="Budget (Max)" icon={DollarSign}>
                <ListPreference items={data.price_ranges} filterType="price_max" />
            </PreferenceCard>

            <PreferenceCard title="Max. Land Size" icon={Maximize}>
                <ListPreference items={data.land_sizes} filterType="land_size" />
            </PreferenceCard>

            <PreferenceCard title="Buying Timeline" icon={Clock}>
                <ListPreference items={data.timelines} filterType="timeline" />
            </PreferenceCard>

            <PreferenceCard title="What describes you?" icon={Lightbulb}>
                <ListPreference items={data.buyer_types} filterType="buyer_type" />
            </PreferenceCard>

            <PreferenceCard title="Finance Status" icon={CreditCard}>
                <div className="space-y-3">
                    {(data.finance_statuses || []).map((item, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => openDrilldown('finance_status', item.name)}
                            className="w-full flex items-center justify-between text-left rounded-md -mx-1 px-1 py-1 hover:bg-blue-50 transition-colors cursor-pointer"
                            title={`Show buyers with ${item.name}`}
                        >
                            <span className="text-sm font-medium text-gray-700 truncate mr-4 hover:text-blue-700">{item.name}</span>
                            <div className="flex items-center gap-2">
                                <div className="w-24 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500"
                                        style={{ width: `${(item.count / data.total_buyers) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs font-bold text-gray-500 w-8 text-right">{item.count}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </PreferenceCard>
        </div>
    );

    const modals = (
        <>
            <BuyerPreferenceMatchesModal
                isOpen={drilldown.open}
                onClose={closeDrilldown}
                filterType={drilldown.filterType}
                filterValue={drilldown.filterValue}
                startDate={startDate}
                endDate={endDate}
                onBuyerClick={onBuyerClick}
            />

            <DeactivatedBuyersModal
                isOpen={showDeactivatedModal}
                onClose={() => setShowDeactivatedModal(false)}
                buyers={data.deactivated_buyers || []}
                rangeApplied={deactivationRangeApplied}
                onBuyerClick={onBuyerClick}
            />
        </>
    );

    if (isGeevee) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <Home className="w-4 h-4 text-[#0891b2]" /> Property Preferences
                </h3>
                <p className="text-xs text-gray-400 mb-4">
                    What buyers want on the portal · {data.total_buyers} with saved preferences · click a row to drill down
                </p>
                {preferenceCharts}
                {modals}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <button
                    type="button"
                    onClick={() => openDrilldown('all', 'all')}
                    title="View all buyers"
                    className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4 text-left w-full transition-all hover:border-blue-200 hover:shadow-md cursor-pointer"
                >
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Total Buyers</p>
                        <p className="text-2xl font-bold text-gray-900">{data.total_buyers}</p>
                        <p className="text-[11px] text-blue-600 font-medium mt-0.5">Click to view list</p>
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setShowDeactivatedModal(true)}
                    title="View deactivated accounts"
                    className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4 text-left w-full transition-all hover:border-red-200 hover:shadow-md cursor-pointer"
                >
                    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                        <UserX className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">
                            Deactivated Accounts
                            <span className="ml-1 text-[10px] uppercase tracking-wider text-gray-400">
                                {deactivationRangeApplied ? 'in range' : 'all time'}
                            </span>
                        </p>
                        <p className="text-2xl font-bold text-gray-900">{deactivatedCount}</p>
                        <p className="text-[11px] text-red-600 font-medium mt-0.5">Click to view list</p>
                    </div>
                </button>
            </div>

            {preferenceCharts}
            {modals}
        </div>
    );
};

export default BuyerAnalytics;
